import React, { ComponentType, useContext, useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { Link } from '~/lib/react-router-dom';
import { LegacyContext } from '~/stormpath/components/LegacyContextType';
import { AuthContext } from '~/stormpath/components/Router';

import { Alert, Autocomplete, Box, Container, Tab, Tabs, TextField } from '@mui/material';

import { GHAPriceCheckContainer } from '~/components/GHAPriceCheck/GHAPriceCheckContainer';
import GHAPriceCompetitivenessContainer from '~/components/GHAPriceCompetitiveness/GHAPriceCompetitivenessContainer';
import HotelGroupView from '~/components/GoogleAdsHotelBidding/HotelGroupView';

import CampaignLinksPage from '~/pages/CampaignLinks/CampaignLinksPage';
import VoucherFormPage from '~/pages/Vouchers/VoucherFormPage';

import tenants from '~/consts/tenant';

import { useUserPromoRoles } from '~/hooks/useUserPromoRoles';

import { PromoRole, promoRoles } from '~/services/PromoService';

import ChannelMarkupContainer from '../ChannelMarkup/ChannelMarkup';
import PageHeader from '../Common/Elements/PageHeader';
import ContentfulFormsContainer from '../ContentfulForms/ContentfulFormsContainer';
import ForcedRankingsConfigContainer from '../ForcedRankingsConfig/ForcedRankingsConfig';
import GHAPropertyManagementContainer from '../GHAPropertyManagement/GHAPropertyManagementContainer';
import BlackhawkGiftCardLogsContainer from '../GiftCards/GiftCardLogs/BlackhawkGiftCardLogsContainer';
import GiftCardLogsContainer from '../GiftCards/GiftCardLogs/GiftCardLogsContainer';
import GiftCardsContainer from '../GiftCards/GiftCards/GiftCardsContainer';
import AdCreativeAIAssistantContainer from '../GoogleAds/AdCreativeAIAssistantContainer';
import BidModificationHistory from '../GoogleAds/BidModificationHistory';
import CustomerListView from '../GoogleAds/CustomerListView';
import GoogleAdsCampaignsConfigContainer from '../GoogleAdsCampaignsConfig/GoogleAdsCampaignsConfig';
import GoogleAdsRuleTemplatesContainer from '../GoogleAdsRuleTemplates/GoogleAdsRuleTemplatesContainer';
import HintCoverageMatrixContainer from '../HintCoverageMatrix/HintCoverageMatrixContainer';
import AgentCommissions from '../LeAgentHub/components/Commission/AgentCommissions';
import AgentQuotes from '../LeAgentHub/components/Quotes/AgentQuotes';
import PinnedOffersContainer from '../PinnedOffers/PinnedOffers';
import PinnedOffersConfigContainer from '../PinnedOffersConfig/PinnedOffersConfig';
import InAppMessageContainer from '../Promo/InAppMessageContainer';
import PromoRequestContainer from '../Promo/PromoRequests/PromoRequestContainer';
import PromoRoleManagement from '../Promo/PromoRoles/PromoRoleManagement';
import PromoSearchContainer from '../Promo/PromoSearchContainer';
import PromoTesterContainer from '../Promo/PromoTester/PromoTesterContainer';
import ReferralLogs from '../Promo/ReferralLogs';
import SimplePromoForm from '../Promo/SimplePromoForm';
import OfferMetricsContainer from '../PromoDisplayConfig/OfferMetricsContainer';
import PromoDisplayConfigContainer from '../PromoDisplayConfig/PromoDisplayConfig';
import LoyaltyPromoKeyOptionsContainer from '../PromoV3/LoyaltyPromoKeyOptionsContainer';
import PromoContainerV3 from '../PromoV3/PromoContainer';
import ReferralEarnOptionsContainer from '../ReferralEarnOptions/ReferralEarnOptionsContainer';
import Tags from '../Tags/Tags';
import MediaViewPage from '../Videos/MediaListPage';
import VoucherUsersSearchPage from '../Vouchers/VoucherUsersSearchPage';
import VouchersContainer from '../Vouchers/Vouchers';

import BadgesContainer from './Badges/BadgesContainer';
import CompedPackagesContainer from './CompedPackages/CompedPackagesContainer';
import ContentSearchPage from './Content/ContentSearchPage';
import FacebookContentImagesContainer from './FacebookContentImages/FacebookContentImagesContainer';
import FacebookImageTemplatesContainer from './FacebookImageTemplates/FacebookImageTemplatesContainer';
import LeLiveContainer from './LeLive/LeLiveContainer';
import OfferDeepLinksContainer from './OfferDeepLinks/OfferDeepLinksContainer';
import CampaignTags from './Tags';
import TemplatesContainer from './Templates/TemplatesContainer';

const CREATE_PROMO_TAB = 'create-promo';
const ADVANCED_PROMO_CREATE = 'advanced-create-promo';
const SEARCH_PROMO_TAB = 'search-promo';
const COMPED_TAB = 'comped';
const OFFER_DEEP_LINKS_TAB = 'offer-deep-links';

type TabConfig = {
  label: string;
  component: ComponentType<unknown>;
  tenants?: Set<string>;
  promoRole?: PromoRole['roleName'];
};

export const tabsMap: Record<string, TabConfig> = {
  'agent-commissions': {
    label: 'Agent Commissions',
    component: AgentCommissions,
    tenants: new Set([tenants.leagenthub.value, tenants.leagenthub_cvfr.value]),
  },
  'agent-quotes': {
    label: 'Agent Quotes',
    component: AgentQuotes,
    tenants: new Set([tenants.leagenthub.value, tenants.leagenthub_cvfr.value]),
  },
  [SEARCH_PROMO_TAB]: {
    label: 'Search Promo Codes',
    component: PromoSearchContainer,
  },
  [CREATE_PROMO_TAB]: {
    label: 'Simple Promo Create',
    component: SimplePromoForm,
  },
  [ADVANCED_PROMO_CREATE]: {
    label: 'Advanced Promo Create',
    component: PromoContainerV3,
  },
  'promo-requests': {
    label: 'Promo Requests',
    component: PromoRequestContainer,
  },
  'promo-tester': {
    label: 'Promo Tester',
    component: PromoTesterContainer,
  },
  'promo-roles': {
    label: 'Promo Roles',
    component: PromoRoleManagement,
  },
  'loyalty-promo-keys': {
    label: 'Promo Loyalty Reward Options',
    component: LoyaltyPromoKeyOptionsContainer,
  },
  'promo-display-config': {
    label: 'Promo Display Config',
    component: PromoDisplayConfigContainer,
  },
  'offer-metrics': {
    label: 'Offer Metrics',
    component: OfferMetricsContainer,
    promoRole: promoRoles.PROMO_BETA_TESTER_ROLE,
  },
  'in-app-messaging': {
    label: 'In App Promo Messaging',
    component: InAppMessageContainer,
  },
  [COMPED_TAB]: {
    label: 'Famils and competition winners',
    component: CompedPackagesContainer,
  },
  'campaign-tags': {
    label: 'Campaign Tags',
    component: CampaignTags,
  },
  'campaign-links': {
    label: 'Campaign Links',
    component: CampaignLinksPage,
  },
  tags: {
    label: 'Tags',
    component: Tags,
  },
  content: {
    label: 'Content',
    component: ContentSearchPage,
  },
  badges: {
    label: 'Badges',
    component: BadgesContainer,
  },
  templates: {
    label: 'Templates',
    component: TemplatesContainer,
  },
  [OFFER_DEEP_LINKS_TAB]: {
    label: 'Offer Deep Links',
    component: OfferDeepLinksContainer,
  },
  'gift-cards': {
    label: 'Gift Cards',
    component: GiftCardsContainer,
  },
  'gift-card-logs': {
    label: 'Gift Card Logs',
    component: GiftCardLogsContainer,
  },
  'blackhawk-gift-card-logs': {
    label: 'Blackhawk Logs',
    component: BlackhawkGiftCardLogsContainer,
  },
  'channel-markup': {
    label: 'Channel Markup',
    component: ChannelMarkupContainer,
  },
  'contentful-forms': {
    label: 'Forms',
    component: ContentfulFormsContainer,
  },
  'google-hotel-ads-property-management': {
    label: 'GHA Property Management',
    component: GHAPropertyManagementContainer,
  },
  'google-hotel-ads-price-check': {
    label: 'GHA Price Comparison',
    component: GHAPriceCheckContainer,
  },
  'google-hotel-ads-price-competitiveness': {
    label: 'GHA Price Competitiveness',
    component: GHAPriceCompetitivenessContainer,
  },
  'hotel-bidding': {
    label: 'Hotel Bidding',
    component: HotelGroupView,
  },
  'bid-modification-history': {
    label: 'Bid Modification History',
    component: BidModificationHistory,
  },
  'google-ads-creative-ai': {
    label: 'Google Ads Creative AI',
    component: AdCreativeAIAssistantContainer,
  },
  'referral-earn-options': {
    label: 'Referral Earn Options',
    component: ReferralEarnOptionsContainer,
  },
  referralLogs: {
    label: 'Referral Logs',
    component: ReferralLogs,
  },
  'google-ads-automation': {
    label: 'Google Ads Automation',
    component: CustomerListView,
  },
  'google-ads-campaigns-config': {
    label: 'Google Ads Campaigns Config',
    component: GoogleAdsCampaignsConfigContainer,
  },
  'google-ads-rule-templates': {
    label: 'Google Ads Rule Templates',
    component: GoogleAdsRuleTemplatesContainer,
  },
  'pull-with-hints-coverage': {
    label: 'Pull With Hints Coverage',
    component: HintCoverageMatrixContainer,
  },
  'forced-rankings-config': {
    label: 'Forced Rankings Config',
    component: ForcedRankingsConfigContainer,
  },
  'pinned-offers-config': {
    label: 'Pinned Offers Config',
    component: PinnedOffersConfigContainer,
  },
  'pinned-offers': {
    label: 'Pinned Offers',
    component: PinnedOffersContainer,
  },
  vouchers: {
    label: 'Vouchers',
    component: VouchersContainer,
  },
  'vouchers-create': {
    label: 'Create Voucher',
    component: VoucherFormPage,
  },
  'voucher-users': {
    label: 'Voucher Users',
    component: VoucherUsersSearchPage,
  },
  'video-gen': {
    label: 'Video Generator',
    component: MediaViewPage,
  },
  'facebook-image-templates': {
    label: 'Facebook Image Templates',
    component: FacebookImageTemplatesContainer,
  },
  'facebook-content-images': {
    label: 'Facebook Content Images',
    component: FacebookContentImagesContainer,
  },
  'le-live': {
    label: 'LE Live',
    component: LeLiveContainer,
  },
} as const;

export default function MarketingPage({ history, match }: RouteComponentProps<{ tab?: string }>) {
  const { user } = useContext<LegacyContext>(AuthContext);
  const key = match.params.tab || SEARCH_PROMO_TAB;

  const { hasRole, isLoading: rolesLoading } = useUserPromoRoles({
    userId: user.id_member,
  });

  const currentTab = tabsMap[key];

  const brand = useSelector((state: App.State) => state.tenant);

  const tabsList = useMemo(() => {
    return Object.keys(tabsMap).filter((key) => {
      const tab = tabsMap[key];

      // If the tab requires a specific promo role
      if (tab.promoRole !== undefined) {
        // If roles are still loading, show all role-based tabs initially
        if (rolesLoading) {
          return true;
        }
        return hasRole(tab.promoRole);
      }

      // For tenant-based filtering, always show
      if (!tab.tenants) {
        return true;
      }
      if (tab.tenants.has(brand.value)) {
        return true;
      }
      return false;
    });
  }, [brand, hasRole, rolesLoading]);

  const title = `Marketing / ${currentTab.label}`;

  const isCurrentTabAccessible = useMemo(() => {
    if (currentTab.promoRole !== undefined) {
      if (rolesLoading) {
        return true;
      }
      return hasRole(currentTab.promoRole);
    }

    return true;
  }, [currentTab, hasRole, rolesLoading]);

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader title="Marketing" />
        <Autocomplete
          options={tabsList}
          value={key}
          onChange={(_, value) => history.push(`/marketing/${value ?? ''}`)}
          getOptionLabel={(option) => tabsMap[option].label}
          renderInput={(params) => <TextField {...params} label="Tab" />}
        />
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs variant="scrollable" scrollButtons allowScrollButtonsMobile value={key}>
            {tabsList.map((tab) => (
              <Tab key={tab} value={tab} label={tabsMap[tab].label} to={`/marketing/${tab}`} component={Link} />
            ))}
          </Tabs>
        </Box>
      </Container>

      {!rolesLoading && !isCurrentTabAccessible && (
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          <Alert severity="error">
            You don't have the role to access this tab. Add the promo role '{currentTab.promoRole}' to your user to
            access this tab.
          </Alert>
        </Container>
      )}

      {(rolesLoading || isCurrentTabAccessible) && <Box component={currentTab.component} />}
    </>
  );
}
